import gsap from 'gsap';

export default class CopyPasteButton extends HTMLElement {
  private code: string | null = null;
  private feedback: HTMLElement | null = null;
  private copiedTimeout: ReturnType<typeof setTimeout> | null = null;
  private defaultAriaLabel: string | null = null;

  private feedbackTl: GSAPTimeline | null = null;

  private onClickBound = this.onClick.bind(this);
  private onKeyDownBound = this.onKeyDown.bind(this);

  connectedCallback(): void {
    this.code = this.getAttribute('data-code');
    this.feedback = this.querySelector('[data-copy-paste-button-ref="feedback"]');
    this.defaultAriaLabel = this.getAttribute('aria-label');

    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'button');
    }

    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    if (!this.defaultAriaLabel) {
      this.setAttribute('aria-label', 'Copy gift card code');
    }

    this.addEventListeners();
  }

  addEventListeners(): void {
    this.addEventListener('click', this.onClickBound);
    this.addEventListener('keydown', this.onKeyDownBound);
  }

  removeEventListeners(): void {
    this.removeEventListener('click', this.onClickBound);
    this.removeEventListener('keydown', this.onKeyDownBound);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    void this.copyCode();
  }

  onClick(): void {
    void this.copyCode();
  }

  async copyCode(): Promise<void> {
    if (!this.code) {
      return;
    }

    try {
      await this.copyToClipboard(this.code);
      this.showCopiedState();
    } catch {
      // Clipboard access can fail in unsupported or restricted contexts.
    }
  }

  async copyToClipboard(text: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }

  showCopiedState(): void {
    this.setAttribute('aria-label', 'Code copied');

    if (this.feedbackTl) {
      this.feedbackTl.kill();
    }

    this.feedbackTl = gsap.timeline({
      onComplete: () => {
        this.feedbackTl = null;
      },
    });

    this.feedbackTl.to(
      this.feedback,
      {
        autoAlpha: 1,
        duration: 0.2,
        ease: 'power2.out',
      },
      0,
    );
    this.feedbackTl.to(
      this.feedback,
      {
        autoAlpha: 0,
        duration: 0.2,
        ease: 'power2.out',
      },
      1,
    );

    if (this.copiedTimeout) {
      clearTimeout(this.copiedTimeout);
    }

    this.copiedTimeout = setTimeout(() => {
      if (this.defaultAriaLabel) {
        this.setAttribute('aria-label', this.defaultAriaLabel);
      } else {
        this.removeAttribute('aria-label');
      }

      this.copiedTimeout = null;
    }, 1000);
  }

  disconnectedCallback(): void {
    this.removeEventListeners();

    if (this.copiedTimeout) {
      clearTimeout(this.copiedTimeout);
      this.copiedTimeout = null;
    }
  }
}

customElements.define('copy-paste-button', CopyPasteButton);
